import React from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle, User, AlertTriangle, Upload, FileText, Info, Eye } from'lucide-react';
import { StepHelpers } from'@shared/components/Stepper';
import { useUserName } from '@shared/selectors/globalSelectors';
import { useUtilityRequestConfiguration } from '../../../../../shared/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { SelectedAccountsBanner } from '../../../../../shared/components/SelectedAccountsBanner';
import { Button } from '@shared/ui/button';
import { useCreateSingleComplaintRequests, useCreateBulkComplaintRequests } from '../../../hooks';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useTranslation } from 'react-i18next';
import { useConsumerDetail } from '../../../../../shared/hook';

export const ReviewSubmitStep = ({
 stepHelpers,
 currentStepIndex = 2,
 onNext,
 onPrevious,
 requestType = "Complaint"
}: {
 stepHelpers?: StepHelpers;
 currentStepIndex?: number;
 onNext?: () => void;
 onPrevious?: () => void;
 remoteUtilityId?: number;
 requestType?: string;
}) => {
 const { t } = useTranslation();
 const step0Data = stepHelpers?.getStepData(currentStepIndex - 2) || {};
 const step1Data = stepHelpers?.getStepData(currentStepIndex - 1) || {};
 const remoteUtilityId = getRemoteUtilityId();

 const location = useLocation();
 const searchParams = new URLSearchParams(location.search);
 const isSubAccount = searchParams.get('isSubAccount') === 'true';
 const urlConsumerId = searchParams.get('consumerId');

 const { data: configData } = useUtilityRequestConfiguration({
  remote_utility_id: remoteUtilityId,
  requestType: requestType,
  disablePagination: true,
 });

 const selectedService = (configData?.result || []).find(s => s.id?.toString() === step0Data.selectedId);
 const category = selectedService?.supportRequestTypeDisplay || selectedService?.requestTypeDisplay || 'NA';
 const subcategory = selectedService?.name || 'NA';
 const complaintName = selectedService?.name || 'NA';

 const incidentDate = step1Data.incidentDate || 'NA';
 const expectedResolution = step1Data.expectedResolution || 'NA';
 const additionalNotes = step1Data.notes || 'NA';
 const evidenceFiles = step1Data.evidenceFiles || [];
 const documentsUploaded = evidenceFiles.length;

 const { data: consumerDetailResult } = useConsumerDetail({
  remote_utility_id: remoteUtilityId,
  consumer_id: urlConsumerId || undefined
 }, { enabled: !!urlConsumerId && isSubAccount });
 const consumerDetail = consumerDetailResult?.result;

 const userNameFallback = useUserName() || 'NA';
 const { remoteConsumerNumber } = getLoginDataFromStorage() || {};
 const accountNumberFallback = remoteConsumerNumber || 'NA';

 const userName = isSubAccount && consumerDetail ? `${consumerDetail.firstName || ''} ${consumerDetail.lastName || ''}`.trim() || 'NA' : userNameFallback;
 const accountNumber = isSubAccount && consumerDetail ? (consumerDetail.consumerNo || 'NA') : accountNumberFallback;
 const serviceAddress = isSubAccount && consumerDetail && consumerDetail.addressData?.service 
  ? `${consumerDetail.addressData.service.address || ''} ${consumerDetail.addressData.service.unit || ''}`.trim() || 'NA' 
  : 'NA';

 const { mutate: createSingleComplaintRequest, isPending: isSinglePending } = useCreateSingleComplaintRequests();
 const { mutate: createBulkComplaintRequest, isPending: isBulkPending } = useCreateBulkComplaintRequests();
 const isSubmitting = isSinglePending || isBulkPending;

  const handleSubmit = () => {
   const isBulk = currentStepIndex === 3;
   const accountsData = isBulk ? stepHelpers?.getStepData(0) || {} : {};
   
   const { consumerId: loggedInConsumerId } = getLoginDataFromStorage() || {};
   const consumerId = isSubAccount && urlConsumerId ? urlConsumerId : loggedInConsumerId;
   const consumerIds = isBulk ? (accountsData.selectedAccounts || []) : (consumerId ? [consumerId] : []);
   
   const formData = new FormData();
   formData.append('remote_utility_id', remoteUtilityId?.toString() || "");
   formData.append('request_type', requestType || "Complaint");
   formData.append('utility_support_request', selectedService?.id?.toString() || "");
   formData.append('consumer_ids', JSON.stringify(consumerIds));
   
   if (evidenceFiles && evidenceFiles.length > 0) {
       evidenceFiles.forEach((fileInfo: any) => {
           const actualFile = (window as any).fileData?.fileObjects?.find((f: any) => f.name === fileInfo.name && f.size === fileInfo.size);
           if (actualFile) {
               formData.append('file', actualFile);
           }
       });
   }

   const mutationOptions = {
    onSuccess: () => {
     if (onNext) onNext();
    },
    onError: (err: any) => {
     console.error("Failed to submit complaint", err);
    }
   };

   if (isBulk) {
     createBulkComplaintRequest(formData, mutationOptions);
   } else {
     createBulkComplaintRequest(formData, mutationOptions);
   }
  };

 return (
  <div className="space-y-4">
   <SelectedAccountsBanner stepHelpers={stepHelpers} />
   {/* Info Banner */}
   <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
    <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
    <p className="text-sm font-medium text-blue-900">{t("Review and submit the complaint")}</p>
   </div>

   {/* Complaint Summary */}
   <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
    <div className="p-4 border-b border-gray-200 flex items-center gap-2">
     <CheckCircle className="w-5 h-5 text-blue-600" />
     <h4 className="font-semibold text-gray-900">{t("Complaint Summary")}</h4>
    </div>
    <div className="grid grid-cols-3 divide-x divide-gray-200 text-center py-6">
     <div>
      <div className="text-blue-600 font-bold text-lg">{complaintName}</div>
      <div className="text-xs text-gray-400 mt-1">{t("Complaint Name")}</div>
     </div>
     <div>
      <div className="font-bold text-lg text-gray-900">{incidentDate}</div>
      <div className="text-xs text-gray-400 mt-1">{t("Incident Date")}</div>
     </div>
     <div>
      <div className="font-bold text-lg text-gray-900">{documentsUploaded}</div>
      <div className="text-xs text-gray-400 mt-1">{t("Evidence Attached")}</div>
     </div>
    </div>
   </div>

   {/* Two Columns */}
   <div className="grid grid-cols-2 gap-4">
    {/* Account Holder */}
    <div className="bg-white border border-gray-200 rounded-xl p-6">
     <div className="flex items-center gap-2 mb-4">
      <User className="w-5 h-5 text-gray-500" />
      <h4 className="font-semibold text-gray-900">{t("Current Account Holder")}</h4>
     </div>
     <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
       <div>
        <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Name")}</div>
        <div className="font-medium text-gray-900">{userName}</div>
       </div>
       <div>
        <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Account Number")}</div>
        <div className="font-medium text-gray-900">{accountNumber}</div>
       </div>
      </div>
      <div>
       <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Service Address")}</div>
       <div className="font-medium text-gray-900">{serviceAddress}</div>
      </div>
     </div>
    </div>

    {/* Complaint Information */}
    <div className="bg-white border border-gray-200 rounded-xl p-6">
     <div className="flex items-center gap-2 mb-4">
      <AlertTriangle className="w-5 h-5 text-gray-500" />
      <h4 className="font-semibold text-gray-900">{t("Complaint Information")}</h4>
     </div>
     <div className="space-y-4">
      <div>
       <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Category")}</div>
       <div className="font-medium text-gray-900">{category}</div>
      </div>
      <div>
       <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Subcategory")}</div>
       <div className="font-medium text-gray-900">{subcategory}</div>
      </div>
     </div>
    </div>
   </div>

   {/* Evidence Configuration */}
   <div className="bg-white border border-gray-200 rounded-xl p-6">
    <div className="flex items-center gap-2 mb-4">
     <Upload className="w-5 h-5 text-gray-500" />
     <h4 className="font-semibold text-gray-900">{t("Evidence")}</h4>
    </div>
    <div className="grid grid-cols-2 gap-4 mb-4">
     <div>
      <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Expected Resolution")}</div>
      <div className="font-medium text-gray-900">{expectedResolution}</div>
     </div>
     <div>
      <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Additional Notes")}</div>
      <div className="font-medium text-gray-900">{additionalNotes}</div>
     </div>
    </div>

    {documentsUploaded === 0 ? (
     <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex items-center gap-2">
      <Info className="w-4 h-4 text-gray-400" />
      <span className="text-sm text-gray-500">{t("No files uploaded.")} <span className="text-blue-600">{t("Add files")}</span> {t("in the previous step.")}</span>
     </div>
    ) : (
     <div className="space-y-2 mt-4">
      <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-2">{t("Uploaded Documents")}</div>
      {evidenceFiles.map((file: any, index: number) => (
       <div key={index} className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 truncate">
         <FileText className="w-4 h-4 text-blue-400 shrink-0" />
         <span className="text-sm text-gray-700 truncate">{file.name || `${t("Document")} ${index + 1}`}</span>
        </div>
        <button 
         type="button" 
         onClick={() => {
           const actualFile = (window as any).fileData?.fileObjects?.find((f: any) => f.name === file.name && f.size === file.size);
           if (actualFile) {
             const url = URL.createObjectURL(actualFile);
             window.open(url, '_blank');
           }
         }}
         className="text-blue-600 hover:text-blue-800 p-1 shrink-0 flex items-center gap-1 transition-colors"
        >
         <Eye className="w-4 h-4" />
         <span className="text-xs font-medium">{t("View")}</span>
        </button>
       </div>
      ))}
     </div>
    )}
   </div>  
  <div className="flex justify-between w-full pt-6 border-t border-gray-200 mt-8">
  <Button
   variant="outline"
   onClick={onPrevious}
   disabled={isSubmitting}
  >
   {t("Previous")}
  </Button>
  <Button
   onClick={handleSubmit}
   disabled={isSubmitting}
  >
   {isSubmitting ? t("Submitting...") : (
     <>
       <CheckCircle className="w-4 h-4 mr-2" /> {t("Submit Request")}
     </>
   )}
  </Button>
  </div>
 </div>
 );
};
